import {
    memo,
    useCallback,
    useEffect,
    type ReactElement,
    type ChangeEvent,
} from 'react';

import type { FormControlProps } from '@mui/material/FormControl';
import type { TextFieldProps } from '@mui/material/TextField';

import { CrossIcon } from '@bestseller-bit/frontend-community.theme.buying-theme';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import Stack from '@mui/material/Stack';
import { alpha, useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { scrollToBottomOfTextField } from './utils';

interface ClearableMultilineTextFieldProps
    extends Omit<
        TextFieldProps,
        'helperText' | 'id' | 'multiline' | 'onChange' | 'size' | 'value'
    > {
    /** Unique identifier for the text field, used for scrolling to the bottom of the field or other purposes */
    id: string;
    /** Label text for the field */
    fieldLabel: string;
    /** Optional content to display on the right side of the label */
    labelRightContent?: ReactElement;
    /** Helper text to display below the text field */
    helperText?: string;
    /** Value of the text field */
    value: string;
    /** Size of the text field */
    size: FormControlProps['size'];
    /** Disable initial scroll to bottom behavior */
    disableInitialScrollToBottom?: boolean;
    /** Is read only */
    isReadOnly?: boolean;
    /** Called when the text field fires its onChange event */
    onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
    /** Called when the custom clear button is clicked */
    onClear?: () => void;
}

const ClearableMultilineTextField = memo(
    ({
        id,
        fieldLabel,
        labelRightContent,
        helperText,
        value,
        error,
        size,
        disableInitialScrollToBottom = false,
        isReadOnly,
        onChange,
        onClear,
        ...textFieldProps
    }: ClearableMultilineTextFieldProps): ReactElement => {
        const { palette } = useTheme();
        const isDisabled = textFieldProps.disabled;

        const shouldShowClearButton =
            !isReadOnly && !isDisabled && value.length > 0;
        const shouldShowHelperText = !!helperText && !isReadOnly && !isDisabled;

        const handleOnChange = useCallback(
            (event: ChangeEvent<HTMLInputElement>): void => {
                onChange?.(event);
            },
            [onChange]
        );

        const handleClearField = useCallback((): void => {
            onClear?.();
        }, [onClear]);

        // Initial scroll to bottom when we render the component
        useEffect(() => {
            if (disableInitialScrollToBottom) return;
            scrollToBottomOfTextField(id);
        }, [disableInitialScrollToBottom, id]);

        return (
            <FormControl
                size={size}
                variant={textFieldProps.variant ?? 'outlined'}
                error={!!error}
                fullWidth
            >
                <Stack
                    direction="row"
                    sx={{
                        justifyContent: 'space-between',
                        alignItems: 'stretch',
                        marginBottom: 1, // Replicating TextField's styling
                    }}
                    useFlexGap
                >
                    <InputLabel
                        disabled={isDisabled}
                        sx={{
                            width: 'fit-content',
                            overflow: 'inherit',
                            textWrap: 'nowrap',
                            marginRight: 5,
                            ...(isReadOnly && {
                                color: `${alpha(palette.primary.main, 0.5)} !important`,
                            }),
                        }}
                    >
                        {fieldLabel}
                    </InputLabel>

                    {labelRightContent}
                </Stack>

                <TextField
                    variant={textFieldProps.variant ?? 'outlined'}
                    {...textFieldProps}
                    error={!!error}
                    size={size}
                    id={id}
                    value={value}
                    slotProps={{
                        input: {
                            ...(textFieldProps.slotProps?.input &&
                                (textFieldProps.slotProps.input as object)),
                            readOnly: isReadOnly,
                            sx: {
                                backgroundColor: isReadOnly
                                    ? 'background.secondary'
                                    : undefined,
                            },
                        },
                    }}
                    multiline
                    onChange={handleOnChange}
                />

                {(shouldShowHelperText || shouldShowClearButton) && (
                    <Stack
                        direction="row"
                        sx={{
                            justifyContent: 'space-between',
                            marginTop: 1, // Replicating TextField's styling
                            height: '16px',
                        }}
                        useFlexGap
                    >
                        {shouldShowHelperText && (
                            <FormHelperText sx={{ marginTop: 0 }}>
                                {helperText}
                            </FormHelperText>
                        )}

                        {shouldShowClearButton && (
                            <Stack
                                direction="row"
                                sx={{
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                }}
                                spacing={1}
                                role="button"
                                tabIndex={0}
                                useFlexGap
                                onClick={handleClearField}
                                onKeyDown={(e): void => {
                                    if (e.key === 'Enter') {
                                        handleClearField();
                                    }
                                }}
                            >
                                <Typography variant="buttons.xsmall">
                                    Clear
                                </Typography>

                                <CrossIcon size={16} />
                            </Stack>
                        )}
                    </Stack>
                )}
            </FormControl>
        );
    }
);

ClearableMultilineTextField.displayName = 'ClearableMultilineTextField';

export { ClearableMultilineTextField };
export type { ClearableMultilineTextFieldProps };
